<template>
  <div class="w-full">
    <TextInput
      :label="$t('Survey Name')"
      :value="name"
      :is-box-background="false"
      input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
      name="name"
      type="text"
      :placeholder="$t('Survey Name')"
      class="mb-4"
      @input="
        ($event) => {
          $emit('update:name', $event.target.value)
        }
      " />

    <div class="flex flex-row items-center gap-2 mb-4 ml-2">
      <SwitchGroup as="div" class="flex items-center justify-between">
        <span class="flex flex-col flex-grow">
          <SwitchLabel as="span" class="text-sm font-medium leading-6 text-gray-900" passive>
            {{ $t("Send with Revsetter email") }}
          </SwitchLabel>
          <SwitchDescription as="span"
                             class="text-sm text-gray-500">{{ $t("Send with Revsetter email") }}
            {{ `<${$store.state.organization.name.toLowerCase()}@revsetter.com>` }}</SwitchDescription>
        </span>
        <Switch
          v-model="use_rs_email"
          :class="[
            use_rs_email ? 'bg-blue' : 'bg-gray-200 dark:bg-dark-screen-background',
            'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
          ]"
          @update:model-value="$emit('update:useRSEmail', use_rs_email)">
          <span
            aria-hidden="true"
            :class="[
              use_rs_email && $store.state.portfolio.ui_options?.language == 'ar'
                ? '-translate-x-5'
                : use_rs_email
                  ? 'translate-x-5'
                  : 'translate-x-0',
              'pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
            ]" />
        </Switch>
      </SwitchGroup>
    </div>

    <div v-if="!use_rs_email" class="p-2 mb-4 border rounded-md dark:border-dark-divider-border">
      <div class="m-1 text-lg text-common-grey">
        {{ $t("Sender") }}:
      </div>
      <div class="flex items-center gap-1 rounded-md">
        <SenderAutoCompleteInput
          :selected-sender="selectedSender"
          :survey="true"
          :placeholder="$t('Sender')"
          input-classes="placeholder-common-grey"
          :senders="senders_list"
          @update:selected-sender="$emit('update:selectedSender', $event)" />
      </div>
      <div class="flex flex-row flex-wrap gap-2 mt-1">
        <!-- For sender -->
        <ContactCard
          v-if="selectedSender?.id"
          :key="selectedSender?.id"
          :survey="true"
          :contact="selectedSender"
          :contact-type="'recipient'"
          :ellipsis-vertical-icon="EllipsisVertical"
          @removeContact="$emit('update:selectedSender', null)" />
      </div>
    </div>

    <div class="flex w-full space-x-2">
      <TextInput
        :label="$t('From label')"
        :value="fromLabel"
        :is-box-background="false"
        input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
        name="form_label"
        type="text"
        :placeholder="$t('From label')"
        @input="
          ($event) => {
            $emit('update:fromLabel', $event.target.value)
          }
        " />

      <div class="w-[120%]">
        <TextInput
          :label="$t('Email Subject')"
          :value="emailSubject"
          :is-box-background="false"
          input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
          name="email_subject"
          type="text"
          :placeholder="$t('Email Subject')"
          @input="
            ($event) => {
              $emit('update:emailSubject', $event.target.value)
            }
          " />
      </div>
    </div>

    <!-- <div class="p-2 mt-2 border rounded-md dark:border-dark-divider-border">
      <div class="m-1 text-lg text-common-grey">
        Recipients:
      </div>
      <div class="flex items-center gap-1 rounded-md">
        <ContactsAutoCompleteInput
          placeholder="Send to"
          input-classes="placeholder-common-grey"
          :selected-contacts="selectedRecords"
          :hide-people-tab="true"
          :hide-companies-tab="true"
          :hide-groups-tab="true"
          @submitted="handleAddItem"
          @chosen="handleAddItem" />
      </div>
      <div class="flex flex-row flex-wrap gap-2 mt-1">
        <ContactCard
          v-for="contact in selectedRecords"
          :key="contact.id"
          :survey="true"
          :contact="contact"
          :contact-type="'recipient'"
          :ellipsis-vertical-icon="EllipsisVertical"
          @removeContact="handleRemoveContact" />
      </div>
    </div> -->

    <div class="rounded-md border-0.5 dark:border-dark-divider-border h-44 w-full flex mb-4 mt-5">
      <TextEditor
        :value="headerMessage"
        :show-label="false"
        :no-border="true"
        toolbar="essential"
        :placeholder="$t('Email body message')"
        name="note_text"
        :label="$t('Email body message')"
        theme="snow"
        input-classes="rounded-b-md"
        @changed="
          (e) => {
            $emit('update:headerMessage', e)
          }
        " />
    </div>

    <div class="rounded-md border-0.5 dark:border-dark-divider-border h-28 w-full flex mt-16">
      <TextArea
        :placeholder="$t('Submit Success Message')"
        :value="successMessage"
        name="success_message"
        class="mt-0 border-0.5 dark:border-dark-divider-border w-full h-full border-1"
        :label="$t('Submit Success Message')"
        @input="$emit('update:successMessage', $event.target.value)" />
    </div>

    <div class="flex items-center space-x-6 my-6">
      <div>{{ $t("Submission End Date") }}:</div>
      <div class="rounded-md shadow-sm">
        <DatePicker
          ref="date_ref_1"
          :model-value="submissionEndDate"
          :is-dark="$store.state.ui.darkMode"
          class="bg-white dark:bg-dark-screen-background"
          is-inline
          mode="date"
          :popover="{ visibility: 'click' }"
          @update:modelValue="$emit('update:submissionEndDate', $event)">
          <template #default="{ inputValue, inputEvents }">
            <div class="relative">
              <svg
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
                class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>

              <input
                id="date_ref"
                :value="inputValue"
                autocomplete="off"
                class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                :class="['bg-light-screen-background dark:bg-dark-box-background']"
                v-on="inputEvents"
                @input="
                  () => {
                    $emit('update:submissionEndDate', $event.target.value)
                  }
                ">
            </div>
          </template>
          <template #footer>
            <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
              <button type="button" class="cancel-btn inline-block" @click.stop="clearDate()">{{ $t("Cancel") }}</button>
            </div>
          </template>
        </DatePicker>
      </div>
    </div>

    <div class="flex space-x-12 items-center">
      <div class="flex flex-row items-center gap-2 ml-2 mb-4 my-6">
        <label class="block text-base font-medium text-gray-700 dark:text-dark-body-text">{{ $t("Allow Resubmission") }}: </label>
        <Switch
          :value="allowResubmission"
          :class="[
            allowResubmission ? 'bg-blue' : 'bg-gray-200 dark:bg-dark-screen-background',
            'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
          ]"
          @update:modelValue="$emit('update:allowResubmission', $event)">
          <span
            aria-hidden="true"
            :class="[
              allowResubmission && $store.state.portfolio.ui_options?.language == 'ar'
                ? '-translate-x-5'
                : allowResubmission
                  ? 'translate-x-5'
                  : 'translate-x-0',
              'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
            ]" />
        </Switch>
      </div>

      <div v-if="allowResubmission" class="flex items-center space-x-6 my-6">
        <div>{{ $t("Resubmission End Date") }}:</div>
        <div class="rounded-md shadow-sm">
          <DatePicker
            ref="date_ref_2"
            :model-value="reSubmissionEndDate"
            :is-dark="$store.state.ui.darkMode"
            class="bg-white dark:bg-dark-screen-background"
            is-inline
            mode="date"
            :popover="{ visibility: 'click' }"
            @update:modelValue="$emit('update:reSubmissionEndDate', $event)">
            <template #default="{ inputValue, inputEvents }">
              <div class="relative">
                <svg
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>

                <input
                  id="date_ref"
                  :value="inputValue"
                  autocomplete="off"
                  class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                  :class="['bg-light-screen-background dark:bg-dark-box-background']"
                  v-on="inputEvents"
                  @input="
                    $utils.debounce(() => {
                      $emit('update:reSubmissionEndDate', $event.target.value)
                    }, 800)
                  ">
              </div>
            </template>
            <template #footer>
              <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                <button type="button" class="cancel-btn inline-block" @click.stop="clearDate()">{{ $t("Cancel") }}</button>
              </div>
            </template>
          </DatePicker>
        </div>
      </div>
    </div>

    <div class="flex justify-between items-center my-4">
      <div class="flex space-x-2 items-center">
        <div>{{ $t("Header Background Color") }}:</div>
        <ColorPickerList :current-item="headerColor" @update:currentItem="$emit('update:headerColor', $event)" />
      </div>

      <div class="flex space-x-2 items-center">
        <div>{{ $t("Header Text Color") }}:</div>
        <ColorPickerList :current-item="headerTextColor" @update:currentItem="$emit('update:headerTextColor', $event)" />
      </div>

      <div class="flex space-x-2 items-center">
        <div>{{ $t("Page Background Color") }}:</div>
        <ColorPickerList :current-item="pageBgColor" @update:currentItem="$emit('update:pageBgColor', $event)" />
      </div>
    </div>

    <TextInput
      :label="$t('Survey Headline')"
      :value="headlineMessage"
      :is-box-background="false"
      input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
      name="headline_message"
      type="text"
      :placeholder="$t('Survey Headline')"
      class="mt-2 mb-4"
      @input="
        ($event) => {
          $emit('update:headlineMessage', $event.target.value)
        }
      " />

    <TextInput
      :label="$t('Survey Subtitle')"
      :value="headlineSubtitleMessage"
      :is-box-background="false"
      input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
      name="headline_subtitle_message"
      type="text"
      :placeholder="$t('Survey Subtitle')"
      class="mt-2 mb-4"
      @input="
        ($event) => {
          $emit('update:headlineSubtitleMessage', $event.target.value)
        }
      " />
  </div>
</template>

<script>
import { DatePicker } from "v-calendar"

import TextEditor from "@/components/shared/TextEditor.vue"

import ColorPickerList from "@/components/shared/ColorPickerList.vue"

import TextInput from "@/components/shared/TextInput.vue"
// import ContactsAutoCompleteInput from "@/components/shared/ContactsAutoCompleteInput.vue"
import ContactCard from "@/components/forms/email/ContactCard.vue"
import { EllipsisVertical } from "lucide-vue-next"
import TextArea from "@/components/shared/TextArea.vue"
import SenderAutoCompleteInput from "@/components/shared/SenderAutoCompleteInput.vue"
import { Switch, SwitchGroup, SwitchDescription, SwitchLabel } from "@headlessui/vue"

export default {
  components: {
    DatePicker,
    TextEditor,
    SwitchGroup,
    SwitchDescription,
    SwitchLabel,
    ColorPickerList,
    Switch,
    TextInput,
    SenderAutoCompleteInput,
    //  ContactsAutoCompleteInput,
    ContactCard,
    // eslint-disable-next-line vue/no-unused-components
    EllipsisVertical,
    TextArea
  },
  props: {
    useRSEmail: null,
    fromLabel: null,
    emailSubject: null,
    headerMessage: null,
    successMessage: null,
    selectedSender: null,
    name: null,
    headerColor: null,
    headerTextColor: null,
    pageBgColor: null,
    headlineMessage: null,
    headlineSubtitleMessage: null,
    submissionEndDate: null,
    reSubmissionEndDate: null,
    allowResubmission: null
  },
  emits: [
    "update:submissionEndDate",
    "update:useRSEmail",
    "update:fromLabel",
    "update:emailSubject",
    "update:selectedRecords",
    "update:headerMessage",
    "update:successMessage",
    "update:name",
    "update:selectedSender",
    "update:headerColor",
    "update:headerTextColor",
    "update:pageBgColor",
    "update:headlineMessage",
    "update:headlineSubtitleMessage",
    "update:allowResubmission",
    "update:reSubmissionEndDate"
  ],
  data() {
    return {
      EllipsisVertical,
      use_rs_email: true
      // selected_records:[],
    }
  },

  computed: {
    senders_list() {
      return this.$store.state.integration.integrations
        .filter((i) => (i.name == "outlook" || i.name == "gmail") && i.status == "on")
        .map((integration) => {
          return { name: integration.user_name, id: integration.id, email: integration.user_email, value: integration.id }
        })
    }
  },
  activated() {
    this.use_rs_email = this.useRSEmail
  },
  created() {
    this.use_rs_email = this.useRSEmail
  },
  methods: {
    clearDate() {
      this.$refs.date_ref_1.hidePopover()
      this.$refs.date_ref_2.hidePopover()
    }
  }
}
</script>
